// src/components/OutlookMail/outlookMailStyles.ts
// Shared color tokens, style definitions, and animation keyframes for OutlookMail components

import React from "react";

export const outlookColors = {
  blue: "#0078D4",
  blueHover: "#106EBE",
  blueBg: "rgba(0, 120, 212, 0.1)",
  blueBorder: "rgba(0, 120, 212, 0.3)",
  mutedBg: "#F8F9FA",
  mutedBgHover: "#E9ECEF",
  border: "#E9ECEF",
  borderDivider: "#E5E7EB",
  foreground: "#1a1a1a",
  mutedForeground: "#6B7280",
  headerText: "#495057",
  separator: "#CED4DA",
  successGreen: "#107C10",
  successBg: "rgba(16, 124, 16, 0.1)",
  successBorder: "rgba(16, 124, 16, 0.3)",
  warningBg: "rgba(255, 193, 7, 0.1)",
  warningColor: "#856404",
  errorBg: "rgba(220, 53, 69, 0.08)",
  errorColor: "#721c24",
  secondaryBg: "#F3F4F6",
  secondaryBgHover: "#E5E7EB",
};

// ─── Email preview card styles ─────────────────────────────────────────

export const previewCardStyle: React.CSSProperties = {
  overflow: "hidden",
  borderRadius: "8px",
  border: `1px solid ${outlookColors.borderDivider}`,
  background: "#FFFFFF",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
};

export const previewHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: `1px solid ${outlookColors.borderDivider}`,
  background: "rgba(243,244,246,0.3)",
  padding: "8px 12px",
};

export const fieldRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  padding: "8px 12px",
  borderBottom: `1px solid ${outlookColors.borderDivider}`,
};

export const fieldLabelStyle: React.CSSProperties = {
  flexShrink: 0,
  paddingTop: "2px",
  fontSize: "12px",
  fontWeight: 500,
  color: outlookColors.mutedForeground,
};

export const recipientChipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  borderRadius: "9999px",
  background: outlookColors.secondaryBg,
  padding: "2px 4px 2px 10px",
  transition: "background-color 0.15s ease",
};

export const recipientChipTextStyle: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 500,
  color: outlookColors.foreground,
};

export const recipientChipRemoveStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "9999px",
  padding: "2px",
  border: "none",
  background: "transparent",
  color: outlookColors.mutedForeground,
  opacity: 0.6,
  cursor: "pointer",
  transition: "opacity 0.15s ease, background-color 0.15s ease",
};

export const inputFieldStyle: React.CSSProperties = {
  flex: 1,
  border: "none",
  background: "transparent",
  fontSize: "13px",
  fontWeight: 500,
  color: outlookColors.foreground,
  outline: "none",
  padding: 0,
  fontFamily: "inherit",
};

export const attachmentRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 12px",
  borderTop: `1px solid ${outlookColors.borderDivider}`,
  cursor: "pointer",
};

export const attachmentBadgeStyle: React.CSSProperties = {
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  borderRadius: "4px",
  background: outlookColors.secondaryBg,
  padding: "4px 8px",
};

export const actionsRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  borderTop: `1px solid ${outlookColors.borderDivider}`,
  background: "rgba(243,244,246,0.2)",
  padding: "10px 12px",
};

export const primaryButtonSendStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 14px",
  borderRadius: "6px",
  border: "none",
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  background: outlookColors.blue,
  color: "#FFFFFF",
  transition: "background-color 0.15s ease",
  fontFamily: "inherit",
};

export const primaryButtonDraftStyle: React.CSSProperties = {
  ...primaryButtonSendStyle,
  background: outlookColors.secondaryBg,
  color: outlookColors.foreground,
};

export const beautifyButtonStyle: React.CSSProperties = {
  ...primaryButtonSendStyle,
  background: "#FFFFFF",
  border: "1px solid #D1D5DB",
  color: "#374151",
  marginLeft: "auto",
};

// ─── Result card styles ────────────────────────────────────────────────

export const resultCardStyle = (isSent: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  borderRadius: "8px",
  border: `1px solid ${isSent ? outlookColors.successBorder : outlookColors.blueBorder}`,
  background: "#FFFFFF",
  padding: "10px 12px",
});

export const resultIconStyle = (isSent: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "9999px",
  background: isSent ? outlookColors.successBg : outlookColors.blueBg,
  flexShrink: 0,
});

export const resultLabelStyle = (isSent: boolean): React.CSSProperties => ({
  fontSize: "12px",
  fontWeight: 600,
  color: isSent ? outlookColors.successGreen : outlookColors.blue,
});

export const resultSubjectStyle: React.CSSProperties = {
  margin: 0,
  marginTop: "2px",
  fontSize: "13px",
  fontWeight: 500,
  color: outlookColors.foreground,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

export const resultMetaStyle: React.CSSProperties = {
  marginTop: "4px",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  columnGap: "8px",
  rowGap: "4px",
  fontSize: "12px",
  color: outlookColors.mutedForeground,
};
